/**
 * Playgroundファイルアップロードコンポーネント
 *
 * ドラッグ&ドロップ対応のファイル選択UI
 */

import {
  Stack,
  Title,
  Text,
  Group,
  Button,
  Card,
  Paper,
  Box,
  Image,
  Center,
} from "@mantine/core";
import { Dropzone, MIME_TYPES, FileRejection } from "@mantine/dropzone";
import { showError } from "../../utils/notifications";
import { Upload, File, X, FileText } from "lucide-react";
import { useCallback } from "react";

interface PlaygroundFileUploaderProps {
  selectedFile: File | null;
  previewUrl: string | null;
  isProcessing: boolean;
  onDrop: (files: File[]) => void;
  onRemoveFile: () => void;
}

export function PlaygroundFileUploader({
  selectedFile,
  previewUrl,
  isProcessing,
  onDrop,
  onRemoveFile,
}: PlaygroundFileUploaderProps) {
  /**
   * ファイル拒否を処理
   */
  const handleReject = useCallback((rejections: FileRejection[]) => {
    const rejection = rejections[0];
    const errorMessage =
      rejection?.errors?.[0]?.message || "対応していないファイル形式です";
    showError(errorMessage, "ファイルエラー");
  }, []);

  return (
    <Card withBorder>
      <Stack gap="md">
        <Title order={5}>1. ファイルをアップロード</Title>

        {!selectedFile ? (
          <Dropzone
            onDrop={onDrop}
            onReject={handleReject}
            maxSize={10 * 1024 * 1024} // 10MB
            accept={[MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png, "image/tiff"]}
            multiple={false}
            disabled={isProcessing}
          >
            <Group
              justify="center"
              gap="xl"
              mih={140}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <Upload size={48} color="var(--mantine-color-blue-6)" />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <X size={48} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <File size={48} color="var(--mantine-color-gray-5)" />
              </Dropzone.Idle>

              <Stack gap={4} align="center">
                <Text size="lg">ここにファイルをドラッグ＆ドロップ</Text>
                <Text size="sm" c="dimmed">
                  PDF、JPEG、PNG、TIFF（最大10MB）
                </Text>
              </Stack>
            </Group>
          </Dropzone>
        ) : (
          <Paper withBorder p="md">
            <Group justify="space-between" align="flex-start">
              <Group gap="md" align="flex-start">
                {/* サムネイルプレビュー */}
                <Paper
                  withBorder
                  p={4}
                  radius="sm"
                  bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                >
                  <Center
                    style={{
                      width: 80,
                      height: 80,
                      overflow: "hidden",
                      borderRadius: 4,
                    }}
                  >
                    {selectedFile.type === "application/pdf" ? (
                      <object
                        data={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        style={{ display: "block" }}
                      >
                        <FileText size={32} color="var(--mantine-color-gray-5)" />
                      </object>
                    ) : (
                      <Image
                        src={previewUrl}
                        fit="contain"
                        w={80}
                        h={80}
                        fallbackSrc=""
                      />
                    )}
                  </Center>
                </Paper>
                {/* ファイル情報 */}
                <Box>
                  <Text fw={500}>{selectedFile.name}</Text>
                  <Text size="xs" c="dimmed">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Text>
                </Box>
              </Group>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<X size={14} />}
                onClick={onRemoveFile}
                disabled={isProcessing}
              >
                削除
              </Button>
            </Group>
          </Paper>
        )}
      </Stack>
    </Card>
  );
}
